import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Plus, X } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import { useTenantMe } from "@/lib/tenant-me";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

/**
 * NewBookingPanel — Client section selector. Three modes:
 *   - empty    : popover-driven typeahead over `/api/v1/customers?q=`
 *   - existing : compact card showing the picked customer + Change button
 *   - new      : the inline 4-input create form, prefilled from the search query
 *
 * The selector is fully controlled — parent owns `value` + `onChange`. The
 * caller mirrors the value into form state so the existing booking POST
 * (email-based upsert) keeps working unchanged.
 */

export type ClientSelectorCustomer = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  lifetimeValue?: number;
};

export type ClientSelectorValue =
  | { kind: "empty" }
  | { kind: "existing"; customer: ClientSelectorCustomer }
  | {
      kind: "new";
      firstName?: string;
      lastName?: string;
      email: string;
      phone?: string;
    };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ClientSelector({
  value,
  onChange,
  emailError,
  popoverContainer,
}: {
  value: ClientSelectorValue;
  onChange: (v: ClientSelectorValue) => void;
  emailError?: string;
  /**
   * Where to portal the search popover. When the selector is rendered
   * inside a modal Drawer/Sheet, pass a node *inside* the Drawer body so
   * scroll inside the popover isn't blocked by Radix Dialog's
   * `react-remove-scroll` shard. Defaults to `document.body`.
   */
  popoverContainer?: HTMLElement | null;
}) {
  if (value.kind === "existing") {
    return (
      <SelectedCard
        customer={value.customer}
        onClear={() => onChange({ kind: "empty" })}
      />
    );
  }
  if (value.kind === "new") {
    return (
      <NewClientInline
        value={value}
        onChange={(next) => onChange(next)}
        onBack={() => onChange({ kind: "empty" })}
        emailError={emailError}
      />
    );
  }
  return (
    <SearchTrigger
      popoverContainer={popoverContainer}
      onPick={(customer) => onChange({ kind: "existing", customer })}
      onCreateNew={(query) =>
        onChange({
          kind: "new",
          email: EMAIL_RE.test(query) ? query : "",
          firstName: !EMAIL_RE.test(query) ? query : undefined,
        })
      }
    />
  );
}

function SearchTrigger({
  onPick,
  onCreateNew,
  popoverContainer,
}: {
  onPick: (customer: ClientSelectorCustomer) => void;
  onCreateNew: (query: string) => void;
  popoverContainer?: HTMLElement | null;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useTenantMe();
  const currency = tenant.data?.tenant.currency ?? "PHP";

  // Debounced via React Query's queryKey — no manual debounce needed.
  // Empty query → top 5 recent clients. Filled query → top 8 matches.
  const list = useQuery({
    queryKey: ["customers", "selector", query],
    enabled: !!accessToken && open,
    queryFn: async () => {
      const q = query.trim();
      const res = await api.GET("/api/v1/customers", {
        params: {
          query: {
            q: q || undefined,
            page: 0,
            size: q ? 8 : 5,
          },
        },
      });
      return unwrap(res);
    },
    staleTime: 10_000,
  });

  const customers = ((list.data?.content ?? []) as ClientSelectorCustomer[]).filter(
    (c) => !!c.id,
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground hover:border-ring/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="truncate">Search by name, email, or phone…</span>
          <span className="ml-auto text-[11px] text-muted-foreground/70">
            Pick existing or add new
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        container={popoverContainer ?? undefined}
        className="p-0 w-[--radix-popover-trigger-width] min-w-[320px]"
        onOpenAutoFocus={(e) => {
          // Let cmdk's CommandInput auto-focus instead of the wrapper.
          e.preventDefault();
        }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, email, or phone…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {list.isLoading && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                Searching…
              </div>
            )}
            {!list.isLoading && customers.length === 0 && (
              <CommandEmpty>
                {query.trim()
                  ? `No clients match "${query.trim()}". Press Enter to add as new.`
                  : "No recent clients yet — type to search or add a new client."}
              </CommandEmpty>
            )}
            {customers.length > 0 && (
              <CommandGroup
                heading={query.trim() ? "Matches" : "Recent clients"}
              >
                {customers.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={c.id}
                    onSelect={() => {
                      onPick(c);
                      setOpen(false);
                      setQuery("");
                    }}
                    className="gap-3 py-2"
                  >
                    <Avatar customer={c} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {customerName(c)}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                    {typeof c.lifetimeValue === "number" &&
                      c.lifetimeValue > 0 && (
                        <span className="text-[11px] tabular-nums text-muted-foreground shrink-0">
                          {formatMoney(currency, c.lifetimeValue, {
                            fractionDigits: 0,
                          })}
                        </span>
                      )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                value="__create_new__"
                onSelect={() => {
                  onCreateNew(query.trim());
                  setOpen(false);
                  setQuery("");
                }}
                className="gap-2 text-primary"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-sm font-medium">
                  {query.trim()
                    ? `Add "${query.trim()}" as new client`
                    : "Add new client"}
                </span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SelectedCard({
  customer,
  onClear,
}: {
  customer: ClientSelectorCustomer;
  onClear: () => void;
}) {
  const tenant = useTenantMe();
  const currency = tenant.data?.tenant.currency ?? "PHP";
  const ltv =
    typeof customer.lifetimeValue === "number" && customer.lifetimeValue > 0
      ? formatMoney(currency, customer.lifetimeValue, { fractionDigits: 0 })
      : null;
  return (
    <div className="rounded-md border bg-primary-light/40 px-3 py-2.5 flex items-start gap-3">
      <Avatar customer={customer} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{customerName(customer)}</p>
        <p className="text-[11px] text-muted-foreground truncate">
          {[customer.email, customer.phone].filter(Boolean).join(" · ") || "—"}
        </p>
        {ltv && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[10px] font-medium px-1.5 py-0.5 tabular-nums">
            Lifetime · {ltv}
          </span>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
        onClick={onClear}
      >
        <X className="h-3 w-3 mr-1" /> Change
      </Button>
    </div>
  );
}

function NewClientInline({
  value,
  onChange,
  onBack,
  emailError,
}: {
  value: Extract<ClientSelectorValue, { kind: "new" }>;
  onChange: (v: Extract<ClientSelectorValue, { kind: "new" }>) => void;
  onBack: () => void;
  emailError?: string;
}) {
  const update = (
    patch: Partial<Extract<ClientSelectorValue, { kind: "new" }>>,
  ) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-md border border-dashed bg-card p-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          New client
        </p>
        <button
          type="button"
          onClick={onBack}
          className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> Search existing
        </button>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cs-firstName">Client name</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input
            id="cs-firstName"
            placeholder="First name"
            value={value.firstName ?? ""}
            onChange={(e) => update({ firstName: e.target.value })}
          />
          <Input
            placeholder="Last name"
            value={value.lastName ?? ""}
            onChange={(e) => update({ lastName: e.target.value })}
          />
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cs-email">Email</Label>
        <Input
          id="cs-email"
          type="email"
          placeholder="name@example.com"
          value={value.email}
          onChange={(e) => update({ email: e.target.value })}
        />
        {emailError && (
          <p className="text-xs text-destructive">{emailError}</p>
        )}
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="cs-phone">Phone</Label>
        <Input
          id="cs-phone"
          placeholder="+63 900 000 0000"
          value={value.phone ?? ""}
          onChange={(e) => update({ phone: e.target.value })}
        />
      </div>
    </div>
  );
}

function Avatar({ customer }: { customer: ClientSelectorCustomer }) {
  const name = customerName(customer);
  const initials =
    name
      .split(" ")
      .map((p) => p[0]?.toUpperCase() ?? "")
      .filter(Boolean)
      .slice(0, 2)
      .join("") || "?";
  return (
    <div className="h-8 w-8 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center text-[11px] font-medium shrink-0">
      {initials}
    </div>
  );
}

function customerName(c: ClientSelectorCustomer): string {
  return (
    [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || "(unnamed)"
  );
}
